import { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import * as yup from "yup";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";
import { useSnackbar } from "notistack";

import { Stack } from "@mui/material";

import {
  createQuotationDraft,
  getAllServiceType,
  getQuotationDraftDetail,
  updateQuotationDraft,
} from "src/api/quotationDraft";
import { searchProduct } from "src/api/product";

import device from "src/styles/device";
import { checkNotEmpty, formatFromElastic } from "src/common";
import { useSteps } from "src/hooks/useStep";

import Button from "src/components/core/Button";

import Main from "src/components/ui/Container";
import Loading from "src/components/ui/Loading";

import Step1 from "./Step1";
import Step2 from "./Step2";

const Container = styled.div`
  width: 100%;
  max-width: 1240px;
  position: relative;
  margin: 0 auto;
  padding: 32px;

  @media ${device.tabletL} {
    padding: 24px 0;
  }
`;

const SpanTitle = styled.div`
  color: #000;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 40px;
  text-align: center;

  @media ${device.tabletL} {
    margin-bottom: 0;
  }
`;

const Content = styled.div`
  width: 100%;

  @media ${device.tabletL} {
    margin-bottom: 120px;
  }
`;

const ButtonGroup = styled(Stack)`
  width: 100%;
  max-width: 1240px;
  height: 72px;
  background-color: var(--white-color);
  z-index: 15;
  margin-top: 24px;

  @media ${device.tabletL} {
    position: fixed;
    bottom: 0;
    width: -webkit-fill-available;
    min-height: 110px;
    padding: 32px 20px;
    border-radius: 24px 24px 0 0;
    box-shadow: 0px -2px 10px 0px rgba(0, 0, 0, 0.1);

    .MuiButton-root {
      padding: 8px 24px;
    }
  }
`;

// const drawerWidth = 260;

// const Main = MatStyled.styled("main", {
//   shouldForwardProp: (prop) => prop !== "open",
// })(({ theme, open }) => ({
//   position: "relative",
//   flexGrow: 1,
//   transition: theme.transitions.create("margin", {
//     easing: theme.transitions.easing.sharp,
//     duration: theme.transitions.duration.leavingScreen,
//   }),
//   marginTop: "75px",
//   width: "100%",
//   height: "calc(100vh - 75px)",
//   overflow: "hidden",
//   overflowY: "scroll",
//   scrollBehavior: "smooth",
//   background: "#fff",
//   marginLeft: `-${drawerWidth}px`,
//   ...(open && {
//     transition: theme.transitions.create("margin", {
//       easing: theme.transitions.easing.easeOut,
//       duration: theme.transitions.duration.enteringScreen,
//     }),
//     marginLeft: 0,
//     position: "relative",
//   }),
//   [theme.breakpoints.down("sm")]: {
//     marginTop: "64px",
//     height: "calc(100vh - 64px)",
//   },
// }));

const phoneRegExp = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

const validationSchema = yup.object().shape({
  project: yup.string().required(),
  contact: yup.object({
    full_name: yup.string().required(),
    email: yup
      .string()
      .email("E-mail is incorrect")
      .test({
        name: "format incorrect",
        message: "E-mail is incorrect",
        test: (value, context) => {
          const regex = /@[^.]*\./;
          if (value !== "" && !regex.test(value)) {
            return false;
          }
          return true;
        },
      }),
    // .matches(/@[^.]*\./, "E-mail is incorrect"),
    // .required("Please enter E-mail"),
    phone_number: yup
      .string()
      .matches(phoneRegExp, "Phone number is not valid")
      .required("Please enter phone number"),
    address: yup.string().required(),
    province: yup.string().required(),
    district: yup.string().required(),
    sub_district: yup.string().required(),
    zipcode: yup.string().required(),
    tel: yup.string(),
    fax: yup.string(),
    tax_id: yup
      .string()
      .when("$customer", ([customer], schema) =>
        customer === "business" ? schema.required() : schema,
      ),
  }),
  company_name: yup
    .string()
    .when("$customer", ([customer], schema) =>
      customer === "business" ? schema.required() : schema,
    ),
  branch: yup.string(),
  document_date: yup.date().required(),
  aging: yup.number().min(1, "Please select aging date").required(),
  expire_date: yup.date(),
  sale_code: yup.string(),

  document_type: yup.string().oneOf(["product", "service", "other"]).required(),
  // quotation_list: yup
  //   .array()
  //   .of(
  //     yup.object({
  //       product_choice_id: yup
  //         .object()
  //         .nullable()
  //         .when('$document', ([document], schema) =>
  //           document === "product" ? schema.required() : schema
  //         ),

  //       service_type: yup
  //         .object()
  //         .nullable()
  //         .when('$document', ([document], schema) =>
  //           document === "service" ? schema.required() : schema
  //         ),

  //       other_code: yup
  //         .string()
  //         .when('$document', ([document], schema) =>
  //           document === "other" ? schema.required() : schema
  //         ),
  //       other_text: yup
  //         .string()
  //         .when('$document', ([document], schema) =>
  //           document === "other" ? schema.required() : schema
  //         ),
  //       other_unit: yup
  //         .string()
  //         .when('$document', ([document], schema) =>
  //           document === "other" ? schema.required() : schema
  //         ),

  //       volume: yup.number().min(1).required(),
  //       total: yup.number().min(1).required(),
  //       price: yup.number().min(1).required(),
  //       discount: yup.number(),

  //       description: yup.string(),
  //     })
  //   )
  //   .min(1)
  //   // .test({
  //   //   name: "required",
  //   //   message: "Quotation list should contain at least one product/service.",
  //   //   test: (array) =>
  //   //     array.every(({ product_choice_id, service_type }, index) => {
  //   //       return (
  //   //         !!service_type || !!product_choice_id
  //   //       );
  //   //     }),
  //   // })
  //   .required(),
  product_list: yup
    .array()
    .of(
      yup.object({
        product_choice_id: yup.object().nullable(),
        // .when('$document', ([document], schema) =>
        //   document === "product" ? schema.required() : schema
        // ),

        volume: yup.number(),
        total: yup.number(),
        price: yup.number(),
        discount: yup.number(),

        description: yup.string(),
      }),
    )
    .test({
      name: "required",
      message: "Product list should not empty product field.",
      test: (array, context) => {
        if (array.length > 1) {
          return array.every(({ product_choice_id }) => !!product_choice_id);
        }
        return true;
      },
    }),
  // .min(1)
  // .required(),
  service_list: yup
    .array()
    .of(
      yup.object({
        service_type: yup
          .object()
          .nullable()
          .when("$document", ([document], schema) =>
            document === "service" ? schema.required() : schema,
          ),

        volume: yup
          .number()
          .when("$document", ([document], schema) =>
            document === "service" ? schema.min(1).required() : schema,
          ),
        total: yup
          .number()
          .when("$document", ([document], schema) =>
            document === "service" ? schema.min(1).required() : schema,
          ),
        price: yup
          .number()
          .when("$document", ([document], schema) =>
            document === "service" ? schema.min(1).required() : schema,
          ),
        discount: yup.number(),

        description: yup.string(),
      }),
    )
    .min(1)
    .required(),
  other_list: yup
    .array()
    .of(
      yup.object({
        other_code: yup.string(),
        // .when('$document', ([document], schema) =>
        //   document === "other" ? schema.required() : schema
        // ),
        other_text: yup.string(),
        // .when('$document', ([document], schema) =>
        //   document === "other" ? schema.required() : schema
        // ),
        other_unit: yup.string(),
        // .when('$document', ([document], schema) =>
        //   document === "other" ? schema.required() : schema
        // ),

        volume: yup.number(),
        total: yup.number(),
        price: yup.number(),
        discount: yup.number(),

        description: yup.string(),
      }),
    )
    .test({
      name: "required",
      message: "Other list should not empty product field.",
      test: (array, context) => {
        if (array.length > 1) {
          return array.every(
            ({ other_code, other_text }) =>
              other_code !== "" && other_text !== "",
          );
        }
        if (array.length === 1) {
          let checkField = array.every(
            ({ other_code, other_text }) =>
              other_code !== "" || other_text !== "",
          );
          if (checkField) {
            return array.every((product) => {
              let requiredValid = [
                "other_code",
                "other_text",
                "other_unit",
                "volume",
                "price",
              ].every((required) => checkNotEmpty(product[required]));
              return requiredValid;
            });
          } else {
            return true;
          }
        }
        return true;
      },
    }),
  // .min(1)
  // .required(),

  remark: yup.string(),
});

const CreateQuoDraft = ({ open }) => {
  let { quotationId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { currentStep, goToNextStep, goToPrevStep } = useSteps({
    initialStep: 0,
    maxStep: 1,
  });

  const [isLoading, setIsLoading] = useState(false);

  const [customerType, setCustomerType] = useState("business");
  const defaultValues = {
    project: "",
    contact: {
      full_name: "",
      email: "",
      address: "",
      province: "",
      district: "",
      sub_district: "",
      zipcode: "",
      phone_number: "",
      tel: "",
      fax: "",
      tax_id: "",
    },
    company_name: "",
    branch: "",

    document_date: new Date(),
    aging: 0,
    expire_date: "",
    sale_code: "",

    document_type: "product", // product, service
    product_list: [
      {
        product_choice_id: null, // ส่งแค่ _id ของ product_choice

        volume: 0,
        price: 0,
        discount: 0,
        total: 0,
        description: "",
      },
    ],
    service_list: [
      {
        service_type: null, // ส่งแค่ _id ของ service_type

        volume: 0,
        price: 0,
        discount: 0,
        total: 0,
        description: "",
      },
    ],
    other_list: [
      {
        other_code: "",
        other_text: "",
        other_unit: "",

        volume: 0,
        price: 0,
        discount: 0,
        total: 0,
        description: "",
      },
    ],

    vat: 0,
    total_vat: 0,
    total_price: 0,
    discount: 0,

    remark: "",
  };
  const [formQuotation, setFormQuotation] = useState(defaultValues);
  const [dataProductList, setDataProductList] = useState([]);
  const [dataServiceType, setDataServiceType] = useState([]);

  let resolver = useYupValidationResolver(validationSchema, {
    customer: customerType,
    document: formQuotation.document_type,
  });

  const {
    formState: { errors: formErrors, isValid },
    control: formControl,
    watch,
    trigger,
    getValues,
    setValue: setFormValue,
    reset,
  } = useForm({
    resolver,
    defaultValues,
  });

  // console.log("formState -> formErrors", formErrors);
  // console.log("formState -> isValid", isValid);

  // const { append: appendElement, remove: removeElement } = useFieldArray({
  //   control: formControl,
  //   name: "quotation_list",
  // });

  const { append: appendProduct, remove: removeProduct } = useFieldArray({
    control: formControl,
    name: "product_list",
  });

  const { append: appendService, remove: removeService } = useFieldArray({
    control: formControl,
    name: "service_list",
  });

  const { append: appendOther, remove: removeOther } = useFieldArray({
    control: formControl,
    name: "other_list",
  });

  const fetchProduct = async () => {
    await searchProduct({}).then(async ({ data }) => {
      setDataProductList(
        data?.rows.length > 0
          ? data?.rows?.map((product) => ({
              ...formatFromElastic(product),
            }))
          : [],
      );
    });
  };

  const fetchServiceType = async () => {
    await getAllServiceType().then(async ({ data }) => {
      setDataServiceType(data);
    });
  };

  useEffect(() => {
    fetchProduct();
    fetchServiceType();
  }, []);

  const fecthQuoData = async () => {
    setIsLoading(true);

    await getQuotationDraftDetail(quotationId)
      .then(async ({ data }) => {
        // console.log('---data---', data);
        setIsLoading(false);
        if (data)
          reset({
            ...data,
            product_list: checkNotEmpty(data?.product_list)
              ? data?.product_list
              : [
                  {
                    product_choice_id: null,
                    volume: 0,
                    price: 0,
                    discount: 0,
                    total: 0,
                    description: "",
                  },
                ],
            service_list: checkNotEmpty(data?.service_list)
              ? data?.service_list
              : [
                  {
                    service_type: null,
                    volume: 0,
                    price: 0,
                    discount: 0,
                    total: 0,
                    description: "",
                  },
                ],
            other_list: checkNotEmpty(data?.other_list)
              ? data?.other_list
              : [
                  {
                    other_code: "",
                    other_text: "",
                    other_unit: "",
                    volume: 0,
                    price: 0,
                    discount: 0,
                    total: 0,
                    description: "",
                  },
                ],
          });
        if (data?.company_name === "") setCustomerType("customer");
      })
      .catch((err) => {
        setIsLoading(false);
        console.log("error", err.response.data.message);
        navigate("/404");
      });
  };

  useEffect(() => {
    if (quotationId === undefined) return;

    fecthQuoData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationId]);

  /**
   * @notice after form value change, then set value to formData state
   */
  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      setFormQuotation(values);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  /**
   * @notice trigger validation when formData updated
   */
  useEffect(() => {
    trigger();
    // console.log("==== formQuotation ====", formQuotation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formQuotation, customerType]);

  /**
   * @notice reset company_name value when customer type change
   */
  useEffect(() => {
    let editingRow = getValues();
    reset({
      ...editingRow,
      company_name: "",
      branch: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerType]);

  useEffect(() => {
    let quoValue = getValues();

    reset({
      ...quoValue,
      total_price: sumProductPrice(),
      discount: sumDiscount(),
      vat: calculateVat(),
      total_vat: sumTotalPrice() + calculateVat(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formQuotation.document_type,
    formQuotation.product_list.length,
    formQuotation.service_list.length,
    formQuotation.other_list.length,
  ]);

  const sumProductPrice = () => {
    // const priceArray = formQuotation?.quotation_list?.map(
    //   (product) => (product.price * product.volume)
    // );
    let quoData = getValues();
    let priceArray = [];
    let otherPriceArray = [];
    if (quoData?.document_type === "service") {
      priceArray = quoData?.service_list?.map(
        (product) => product.price * product.volume,
      );
    } else if (quoData?.document_type === "product") {
      priceArray = quoData?.product_list?.map(
        (product) => product.price * product.volume,
      );
      otherPriceArray = quoData?.other_list?.map(
        (product) => product.price * product.volume,
      );
    }
    return (
      [...priceArray, ...otherPriceArray]?.reduce((a, b) => a + b, 0) || 0 || 0
    );
  };

  const sumDiscount = () => {
    // const priceArray = formQuotation?.quotation_list?.map(
    //   (product) => (product.discount)
    // );
    let quoData = getValues();
    let priceArray = [];
    let otherPriceArray = [];
    if (quoData?.document_type === "service") {
      priceArray = quoData?.service_list?.map((product) => product.discount);
    } else if (quoData?.document_type === "product") {
      priceArray = quoData?.product_list?.map((product) => product.discount);
      otherPriceArray = quoData?.other_list?.map((product) => product.discount);
    }
    return [...priceArray, ...otherPriceArray]?.reduce((a, b) => a + b, 0) || 0;
  };

  const sumTotalPrice = () => {
    return sumProductPrice() - sumDiscount() || 0;
  };

  const calculateVat = () => {
    return ((sumProductPrice() - sumDiscount()) * 7) / 100 || 0;
  };

  const productValid = formQuotation.product_list.every(
    ({ product_choice_id }) => {
      return !!product_choice_id;
    },
  );

  const otherValid = formQuotation.other_list.every((list) => {
    let requiredValid = [
      "other_code",
      "other_text",
      "other_unit",
      "volume",
      "price",
    ].every((required) => checkNotEmpty(list[required]));
    return requiredValid;
  });

  // console.log('=== valid ===', productValid, '------', otherValid)

  const handleChange = async (event, replaceName, replaceValue) => {
    // console.log('---- e ---', event, '===', replaceName, '--- replaceValue ---', replaceValue)
    let name =
      typeof replaceName !== "undefined" ? replaceName : event.target.name;
    let value =
      typeof replaceValue !== "undefined" ? replaceValue : event.target.value;

    let path = name.split(".");
    let [key, ...rest] = path;

    let editingKey = key;
    let updated;

    if (key === "old_customer") {
      let editingRow = getValues();

      if (value === null) {
        reset(defaultValues);
      }

      reset({
        ...editingRow,
        contact: {
          ...editingRow.contact,
          full_name: value?.contact?.full_name || "",
          email: value?.email,
          phone_number: value?.contact?.phone_number || "",
          tax_id: value?.contact?.tax_id || "",
          ...(checkNotEmpty(value?.contact?.province) && {
            address: value?.contact?.address || "",
            province: value?.contact?.province || "",
            district: value?.contact?.district || "",
            sub_district: value?.contact?.sub_district || "",
            zipcode: value?.contact?.zipcode || "",
          }),
        },
        ...(customerType === "business" &&
          checkNotEmpty(value?.contact?.company_name) && {
            company_name: value?.contact?.company_name || "",
            branch: value?.contact?.branch || "",
          }),
      });
      return;
    } else if (key === "contact") {
      let [objKey] = rest;
      let editingRow = getValues(`contact`);

      if (objKey === "province") {
        updated = {
          ...editingRow,
          province: value,
          district: "",
          sub_district: "",
          zipcode: "",
        };
      } else if (objKey === "district") {
        updated = {
          ...editingRow,
          district: value,
          sub_district: "",
          zipcode: "",
        };
      } else if (objKey === "sub_district") {
        updated = {
          ...editingRow,
          sub_district: value,
        };
      } else {
        updated = {
          ...editingRow,
          [objKey]: value,
        };
      }
    } else if (key === "document_date") {
      let agind_date = getValues(`aging`);
      if (agind_date > 0) {
        let doc_date = value.$d;
        let exp_date = new Date(doc_date);
        exp_date.setDate(doc_date.getDate() + agind_date);

        setFormValue("expire_date", exp_date);
      }
      updated = value.$d;
    } else if (key === "aging") {
      let doc_date = new Date(getValues(`document_date`));
      let exp_date = new Date(doc_date);
      exp_date.setDate(doc_date.getDate() + value);

      setFormValue("expire_date", exp_date);

      updated = value;
    } else if (key === "document_type") {
      let editingRow = getValues();
      reset({
        ...editingRow,
        // quotation_list: [
        //   {
        //     product_choice_id: null,
        //     service_type: null,
        //     other_code: "",
        //     other_text: "",
        //     other_unit: "",

        //     volume: 0,
        //     price: 0,
        //     discount: 0,
        //     total: 0,
        //     description: "",
        //   },
        // ],
        product_list: [
          {
            product_choice_id: null,

            volume: 0,
            price: 0,
            discount: 0,
            total: 0,
            description: "",
          },
        ],
        service_list: [
          {
            service_type: null,

            volume: 0,
            price: 0,
            discount: 0,
            total: 0,
            description: "",
          },
        ],
        other_list: [
          {
            other_code: "",
            other_text: "",
            other_unit: "",

            volume: 0,
            price: 0,
            discount: 0,
            total: 0,
            description: "",
          },
        ],
      });
      updated = value;
    }
    // else if (key === "quotation_list") {
    //   let [objIndex, objKey, subObjKey] = rest;
    //   editingKey = `${key}.${objIndex}`;
    //   let editingRow = getValues(editingKey);

    //   // console.log('---- editingRow ---', editingRow, '--- value ---', value);

    //   if (value === null) {
    //     updated = {
    //       product_choice_id: null,
    //       service_type: null,
    //       other_code: "",
    //       other_text: "",
    //       other_unit: "",
    //       volume: 0,
    //       price: 0,
    //       discount: 0,
    //       total: 0,
    //       description: "",
    //     };
    //   } else if (objKey === "product_choice_id") {
    //     updated = {
    //       ...editingRow,
    //       product_choice_id: value,
    //       volume: 1,
    //       price: value.group_price?.is_start_price
    //         ? value.group_price?.start_price
    //         : value.group_price?.special_price > 0 &&
    //           value.group_price?.special_price !== null &&
    //           value.group_price?.special_price !== undefined
    //         ? value.group_price?.special_price
    //         : value.group_price?.price,
    //       discount: 0,
    //       total: value.group_price?.is_start_price
    //         ? value.group_price?.start_price
    //         : value.group_price?.special_price > 0 &&
    //           value.group_price?.special_price !== null &&
    //           value.group_price?.special_price !== undefined
    //         ? value.group_price?.special_price
    //         : value.group_price?.price,
    //       description: "",
    //     };
    //   } else if (objKey === "service_type") {
    //     updated = {
    //       ...editingRow,
    //       service_type: value,
    //       volume: 1,
    //     };
    //   } else if (objKey === "volume") {
    //     updated = {
    //       ...editingRow,
    //       volume: value,
    //       total: (+editingRow.price * (+value)) - (+editingRow.discount),
    //     };
    //   } else if (objKey === "price") {
    //     updated = {
    //       ...editingRow,
    //       price: value,
    //       total: ((+value) * (+editingRow.volume)) - (+editingRow.discount),
    //     };
    //   } else if (objKey === "discount") {
    //     updated = {
    //       ...editingRow,
    //       discount: value,
    //       total: (+editingRow.price * (+editingRow.volume)) - (+value),
    //     };
    //   } else if (objKey === "edit") {
    //     if (subObjKey === "product_choice_id") {
    //       updated = {
    //         ...editingRow,
    //         product_choice_id: value?.product_choice_id,
    //         volume: value?.volume,
    //         price: value?.price,
    //         discount: value?.discount,
    //         total: value?.total,
    //       };
    //     } else if (subObjKey === "service_type") {
    //       updated = {
    //         ...editingRow,
    //         service_type: value?.service_type,
    //         volume: value?.volume,
    //         price: value?.price,
    //         discount: value?.discount,
    //         total: value?.total,
    //       };
    //     } else if (subObjKey === "other") {
    //       updated = {
    //         ...editingRow,
    //         other_code: value?.other_code,
    //         other_text: value?.other_text,
    //         other_unit: value?.other_unit,
    //         volume: value?.volume,
    //         price: value?.price,
    //         discount: value?.discount,
    //         total: value?.total,
    //       };
    //     }
    //   } else {
    //     updated = {
    //       ...editingRow,
    //       [objKey]: value,
    //     }
    //   }
    // }
    else if (key === "product_list") {
      let [objIndex, objKey, subObjKey] = rest;
      editingKey = `${key}.${objIndex}`;
      let editingRow = getValues(editingKey);

      // console.log('---- editingRow ---', editingRow, '--- value ---', value);

      if (value === null) {
        updated = {
          product_choice_id: null,
          volume: 0,
          price: 0,
          discount: 0,
          total: 0,
          description: "",
        };
      } else if (objKey === "product_choice_id") {
        updated = {
          ...editingRow,
          product_choice_id: value,
          volume: 1,
          price: value.group_price?.is_start_price
            ? value.group_price?.start_price
            : value.group_price?.special_price > 0 &&
                value.group_price?.special_price !== null &&
                value.group_price?.special_price !== undefined
              ? value.group_price?.special_price
              : value.group_price?.price,
          discount: 0,
          total: value.group_price?.is_start_price
            ? value.group_price?.start_price
            : value.group_price?.special_price > 0 &&
                value.group_price?.special_price !== null &&
                value.group_price?.special_price !== undefined
              ? value.group_price?.special_price
              : value.group_price?.price,
          description: "",
        };
      } else if (objKey === "volume") {
        updated = {
          ...editingRow,
          volume: value,
          total: +editingRow.price * +value - +editingRow.discount,
        };
      } else if (objKey === "price") {
        updated = {
          ...editingRow,
          price: value,
          total: +value * +editingRow.volume - +editingRow.discount,
        };
      } else if (objKey === "discount") {
        updated = {
          ...editingRow,
          discount: value,
          total: +editingRow.price * +editingRow.volume - +value,
        };
      } else if (objKey === "edit") {
        if (subObjKey === "product_choice_id") {
          updated = {
            ...editingRow,
            product_choice_id: value?.product_choice_id,
            volume: value?.volume,
            price: value?.price,
            discount: value?.discount,
            total: value?.total,
          };
        }
      } else {
        updated = {
          ...editingRow,
          [objKey]: value,
        };
      }
    } else if (key === "service_list") {
      let [objIndex, objKey, subObjKey] = rest;
      editingKey = `${key}.${objIndex}`;
      let editingRow = getValues(editingKey);

      // console.log('---- editingRow ---', editingRow, '--- value ---', value);

      if (value === null) {
        updated = {
          service_type: null,
          volume: 0,
          price: 0,
          discount: 0,
          total: 0,
          description: "",
        };
      } else if (objKey === "service_type") {
        updated = {
          ...editingRow,
          service_type: value,
          volume: 1,
        };
      } else if (objKey === "volume") {
        updated = {
          ...editingRow,
          volume: value,
          total: +editingRow.price * +value - +editingRow.discount,
        };
      } else if (objKey === "price") {
        updated = {
          ...editingRow,
          price: value,
          total: +value * +editingRow.volume - +editingRow.discount,
        };
      } else if (objKey === "discount") {
        updated = {
          ...editingRow,
          discount: value,
          total: +editingRow.price * +editingRow.volume - +value,
        };
      } else if (objKey === "edit") {
        if (subObjKey === "service_type") {
          updated = {
            ...editingRow,
            service_type: value?.service_type,
            volume: value?.volume,
            price: value?.price,
            discount: value?.discount,
            total: value?.total,
          };
        }
      } else {
        updated = {
          ...editingRow,
          [objKey]: value,
        };
      }
    } else if (key === "other_list") {
      let [objIndex, objKey, subObjKey] = rest;
      editingKey = `${key}.${objIndex}`;
      let editingRow = getValues(editingKey);

      // console.log('---- editingRow ---', editingRow, '--- value ---', value);

      if (value === null) {
        updated = {
          other_code: "",
          other_text: "",
          other_unit: "",
          volume: 0,
          price: 0,
          discount: 0,
          total: 0,
          description: "",
        };
      } else if (objKey === "volume") {
        updated = {
          ...editingRow,
          volume: value,
          total: +editingRow.price * +value - +editingRow.discount,
        };
      } else if (objKey === "price") {
        updated = {
          ...editingRow,
          price: value,
          total: +value * +editingRow.volume - +editingRow.discount,
        };
      } else if (objKey === "discount") {
        updated = {
          ...editingRow,
          discount: value,
          total: +editingRow.price * +editingRow.volume - +value,
        };
      } else if (objKey === "edit") {
        if (subObjKey === "other") {
          updated = {
            ...editingRow,
            other_code: value?.other_code,
            other_text: value?.other_text,
            other_unit: value?.other_unit,
            volume: value?.volume,
            price: value?.price,
            discount: value?.discount,
            total: value?.total,
          };
        }
      } else {
        updated = {
          ...editingRow,
          [objKey]: value,
        };
      }
    } else {
      updated = value;
    }

    if (editingKey !== "") {
      await setFormValue(editingKey, updated);
    }

    let quoValue = await getValues();

    await reset({
      ...quoValue,
      total_price: sumProductPrice(),
      discount: sumDiscount(),
      vat: calculateVat(),
      total_vat: sumTotalPrice() + calculateVat(),
    });
  };

  const getStepComponent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Step1
            customerType={customerType}
            onChangeCustomerType={setCustomerType}
            formErrors={formErrors}
            formData={formQuotation}
            onChange={handleChange}
            dataProductList={dataProductList}
            dataServiceType={dataServiceType}
            // onAppendElement={() => {
            //   appendElement({
            //     product_choice_id: null,
            //     service_type: null,
            //     other_code: "",
            //     other_text: "",
            //     other_unit: "",

            //     volume: 0,
            //     price: 0,
            //     discount: 0,
            //     total: 0,
            //     description: "",
            //   });
            // }}
            // onAppendData={(data) => {
            //   appendElement(data);
            // }}
            // onRemoveElement={removeElement}
            onAppendProduct={() => {
              appendProduct({
                product_choice_id: null,

                volume: 0,
                price: 0,
                discount: 0,
                total: 0,
                description: "",
              });
            }}
            onAppendProductData={(data) => {
              appendProduct(data);
            }}
            onRemoveProduct={removeProduct}
            onAppendService={() => {
              appendService({
                service_type: null,

                volume: 0,
                price: 0,
                discount: 0,
                total: 0,
                description: "",
              });
            }}
            onAppendServiceData={(data) => {
              appendService(data);
            }}
            onRemoveService={removeService}
            onAppendOther={() => {
              appendOther({
                other_code: "",
                other_text: "",
                other_unit: "",

                volume: 0,
                price: 0,
                discount: 0,
                total: 0,
                description: "",
              });
            }}
            onAppendOtherData={(data) => {
              appendOther(data);
            }}
            onRemoveOther={removeOther}
          />
        );
      case 1:
        return (
          <Step2
            formErrors={formErrors}
            formData={formQuotation}
            onChange={handleChange}
            dataProductList={dataProductList}
            dataServiceType={dataServiceType}
          />
        );
      default:
        return null;
    }
  };

  const handleToNextStep = () => {
    let quoValue = getValues();

    reset({
      ...quoValue,
      total_price: sumProductPrice(),
      discount: sumDiscount(),
      vat: calculateVat(),
      total_vat: sumTotalPrice() + calculateVat(),
    });

    goToNextStep();
  };

  const handleSave = async () => {
    setIsLoading(true);

    // let data = new FormData();

    const quoDraftParams = {
      ...formQuotation,
      // quotation_list: formQuotation.quotation_list.map(
      //   (product) => {
      //     return ({
      //       ...product,
      //       ...(product.product_choice_id !== null && {
      //         product_choice_id: product.product_choice_id._id
      //       }),
      //       ...(product.service_type !== null && {
      //         service_type: product.service_type._id
      //       }),
      //     })
      //   }
      // ),
      product_list: checkNotEmpty(
        formQuotation.product_list[0]?.product_choice_id,
      )
        ? formQuotation.product_list.map((product) => {
            return {
              ...product,
              ...(product.product_choice_id !== null && {
                product_choice_id: product.product_choice_id._id,
              }),
            };
          })
        : [],
      service_list: checkNotEmpty(formQuotation.service_list[0]?.service_type)
        ? formQuotation.service_list.map((product) => {
            return {
              ...product,
              ...(product.service_type !== null && {
                service_type: product.service_type._id,
              }),
            };
          })
        : [],
      other_list:
        checkNotEmpty(formQuotation.other_list[0]?.other_code) &&
        checkNotEmpty(formQuotation.other_list[0]?.other_text)
          ? formQuotation.other_list
          : [],
    };

    // Object.keys(quoDraftParams).forEach((key) => {
    //   if (Array.isArray(quoDraftParams[key]) || typeof quoDraftParams[key] === 'object') {
    //     data.append(key, JSON.stringify(quoDraftParams[key]));
    //   } else {
    //     data.append(key, quoDraftParams[key]);
    //   }
    //   return data;
    // });

    if (quotationId) {
      await updateQuotationDraft(quoDraftParams)
        .then((res) => {
          setIsLoading(false);
          enqueueSnackbar("Update Success!", {
            variant: "success",
          });
          setTimeout(() => {
            navigate(`/sale-quotation/detail/${quotationId}`);
          }, 1500);
        })
        .catch((err) => {
          setIsLoading(false);
          console.log("error: ", err.response);
          enqueueSnackbar(err.response.data.message, {
            variant: "error",
          });
        });
    } else {
      await createQuotationDraft(quoDraftParams)
        .then((res) => {
          setIsLoading(false);
          enqueueSnackbar("Create Success!", {
            variant: "success",
          });
          setTimeout(() => {
            navigate(`/sale-quotation/detail/${res.data.quotation_id}`);
          }, 1500);
        })
        .catch((err) => {
          setIsLoading(false);
          console.log("error: ", err.response);
          enqueueSnackbar(err.response.data.message, {
            variant: "error",
          });
        });
    }
  };

  const scrollRef = useRef(null);

  const scrollToTop = () => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo(0, 0);
  };

  useEffect(() => {
    scrollToTop();
  }, [currentStep]);

  return (
    <>
      <Main bg="white" open={open} ref={scrollRef}>
        <Container>
          <SpanTitle>สร้างใบเสนอราคาชั่วคราว</SpanTitle>

          {/* component */}
          <Content>{getStepComponent()}</Content>

          <ButtonGroup
            flexDirection={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            columnGap={2}
          >
            {currentStep === 0 ? (
              <>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleToNextStep()}
                  disabled={
                    !isValid ||
                    (formQuotation.document_type === "product" &&
                      !productValid &&
                      !otherValid)
                  }
                  sx={{ width: "100%", maxWidth: 350, borderRadius: 4 }}
                >
                  Next
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="large"
                  onClick={() => goToPrevStep()}
                  sx={{
                    width: "100%",
                    maxWidth: 160,
                    borderRadius: 4,
                  }}
                >
                  Edit
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  disabled={formQuotation?.remark?.length > 400}
                  onClick={() => handleSave()}
                  sx={{ width: "100%", maxWidth: 160, borderRadius: 4 }}
                >
                  Save
                </Button>
              </>
            )}
          </ButtonGroup>
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default CreateQuoDraft;
